import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { RegisterRequest, UserRole } from '../types/auth';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { APP_DATE_FORMAT } from '../utils/formatters';
import { APP_ROUTES } from '../routing/appRoutes';

export const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isEmployee, setIsEmployee] = useState(false);

  const [formData, setFormData] = useState<RegisterRequest>({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    dateOfBirth: '',
    driverLicenseNumber: '',
    licenseExpiryDate: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    role: UserRole.USER,
    companyId: undefined,
    employeeId: '',
    department: '',
    hireDate: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.firstName.trim()) newErrors.firstName = t('register.validation.firstNameRequired');
    if (!formData.lastName.trim()) newErrors.lastName = t('register.validation.lastNameRequired');
    if (!formData.username.trim()) newErrors.username = t('register.validation.usernameRequired', 'Username is required');
    if (!formData.password) newErrors.password = t('register.validation.passwordRequired');
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = t('register.validation.phoneRequired');

    // Password validation
    if (formData.password && formData.password.length < 6) {
      newErrors.password = t('profile.passwordLength');
    }

    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.passwordMatch');
    }

    // Phone number validation
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    if (formData.phoneNumber && !phoneRegex.test(formData.phoneNumber.replace(/[\s\-()]/g, ''))) {
      newErrors.phoneNumber = t('validation.phoneNumber');
    }

    // Employee-specific validations
    if (isEmployee) {
      if (!formData.employeeId?.trim()) newErrors.employeeId = t('register.validation.employeeIdRequired');
      if (!formData.department?.trim()) newErrors.department = t('register.validation.departmentRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare data for API (remove confirmPassword)
      const { confirmPassword, ...registerData } = formData;
      
      const user = await register(registerData);
      setSuccess(true);
      
      // Redirect based on user role after a short delay
      setTimeout(() => {
        if (user?.role === 'SUPERUSER' || user?.role === 'ADMIN' || user?.role === 'USER') {
          navigate(APP_ROUTES.company.dashboard);
        } else {
          navigate(APP_ROUTES.user.dashboard);
        }
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data?.error || t('register.failed'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('register.success')}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            {t('register.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            {t('register.subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* User Type Selection */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isEmployee}
                    onChange={(e) => setIsEmployee(e.target.checked)}
                  />
                }
                label={t('register.employeeToggle')}
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Basic Information */}
            <Typography variant="h6" gutterBottom>
              {t('register.basicInformation')}
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.firstName')}
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.lastName')}
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.username', 'Username')}
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  error={!!errors.username}
                  helperText={errors.username}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.phoneNumber')}
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label={t('fields.dateOfBirth')}
                  value={formData.dateOfBirth ? dayjs(formData.dateOfBirth) : null}
                  onChange={(date) => handleInputChange('dateOfBirth', date?.format('YYYY-MM-DD') || '')}
                  format={APP_DATE_FORMAT}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Password Section */}
            <Typography variant="h6" gutterBottom>
              {t('register.security')}
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.password')}
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  error={!!errors.password}
                  helperText={errors.password}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.confirmPassword')}
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  required
                />
              </Grid>
            </Grid>

            {/* Address Information */}
            <Typography variant="h6" gutterBottom>
              {t('register.addressInformation')}
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('fields.streetAddress')}
                  value={formData.streetAddress}
                  onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('fields.city')}
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('fields.state')}
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('fields.postalCode')}
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('fields.country')}
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                />
              </Grid>
            </Grid>

            {/* Driver License Information */}
            <Typography variant="h6" gutterBottom>
              {t('register.driverLicenseInformation')}
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.driverLicenseNumber')}
                  value={formData.driverLicenseNumber}
                  onChange={(e) => handleInputChange('driverLicenseNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label={t('fields.licenseExpiryDate')}
                  value={formData.licenseExpiryDate ? dayjs(formData.licenseExpiryDate) : null}
                  onChange={(date) => handleInputChange('licenseExpiryDate', date?.format('YYYY-MM-DD') || '')}
                  format={APP_DATE_FORMAT}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Employee Information (if applicable) */}
            {isEmployee && (
              <>
                <Typography variant="h6" gutterBottom>
                  {t('register.employeeInformation')}
                </Typography>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('fields.employeeId')}
                      value={formData.employeeId}
                      onChange={(e) => handleInputChange('employeeId', e.target.value)}
                      error={!!errors.employeeId}
                      helperText={errors.employeeId}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('fields.department')}
                      value={formData.department}
                      onChange={(e) => handleInputChange('department', e.target.value)}
                      error={!!errors.department}
                      helperText={errors.department}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label={t('fields.hireDate')}
                      value={formData.hireDate ? dayjs(formData.hireDate) : null}
                      onChange={(date) => handleInputChange('hireDate', date?.format('YYYY-MM-DD') || '')}
                      format={APP_DATE_FORMAT}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>{t('fields.role')}</InputLabel>
                      <Select
                        value={formData.role}
                        onChange={(e) => handleInputChange('role', e.target.value)}
                        label={t('fields.role')}
                      >
                        <MenuItem value={UserRole.USER}>{t('roles.user')}</MenuItem>
                        <MenuItem value={UserRole.ADMIN}>{t('roles.admin')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ minWidth: 200 }}
              >
                {loading ? <CircularProgress size={24} /> : t('auth.createAccount')}
              </Button>
            </Box>

            {/* Login Link */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t('register.alreadyHaveAccount')}{' '}
                <Button
                  variant="text"
                  onClick={() => navigate(APP_ROUTES.auth.login)}
                  sx={{ textTransform: 'none' }}
                >
                  {t('register.signInHere')}
                </Button>
              </Typography>
    </Box>
          </form>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};
